import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NoSymptomsDialogComponent } from './no-symptoms-dialog/no-symptoms-dialog.component';
import { RestService } from '../services/rest.service';
import { LoginService } from '../services/login.service';
import { loaderComponent } from '../shared/components/loader/loader.component';
import { SessionService } from '../services/session.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { TranslationService } from '../services/translation.service';
import { TestInProgressComponent } from '../guards/test-in-progress.guard';

type YesNoValue = 'yes' | 'no' | 'prefer_not_to_say';

// The gynaecological-surgery branch that opens under "Surgical procedure".
// Two of the four procedures remove ovaries, which is what gates the
// follow-up count question.
type SurgeryProcedure =
  | 'hysterectomy_only'
  | 'ovaries_only'
  | 'hysterectomy_and_ovaries'
  | 'not_sure';
const PROCEDURES_WITH_OVARY_REMOVAL: SurgeryProcedure[] = [
  'ovaries_only',
  'hysterectomy_and_ovaries',
];

@Component({
  selector: 'app-symptoms',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    loaderComponent,
    TranslatePipe,
  ],
  templateUrl: './symptoms.component.html',
  styleUrls: ['./symptoms.component.scss'],
})
export class SymptomsComponent implements TestInProgressComponent {
  /**
   * F20: the intake answers live only in the form until Continue is pressed, so
   * leaving now (bottom nav, header back, device back) discards them. Reported
   * to `testInProgressGuard`, which asks for confirmation first. Never true
   * while the submit is in flight, so the funnel's own forward navigation is
   * not interrupted.
   */
  isTestInProgress(): boolean {
    if (this.isSubmitting) return false;
    // NOT symptomsForm.dirty. Every Yes/No and every chip on this screen is a
    // <div (click)> that calls setValue(), and setValue() does not mark a
    // control dirty - only input through a control value accessor does. So the
    // only two controls that could ever set the flag were the age and free-text
    // <input>s, and a screen full of tapped answers still looked untouched and
    // was discarded without asking. Read the VALUES instead, the way the
    // symptom sweep already does.
    const v = this.symptomsForm.value;
    const answered = (x: unknown) => x !== null && x !== undefined && x !== '';
    return (
      this.noneSelected ||
      answered(v.age) ||
      answered(v.period_last_12) ||
      answered(v.last_period) ||
      ((v.noBleedReasons as string[] | null)?.length ?? 0) > 0 ||
      this.symptomChips.some((c) => !!v[c.key]) ||
      !!v.other_enabled ||
      answered(v.other_text)
    );
  }

  symptomsForm: FormGroup;
  scanId: string;
  isSubmitting = false;
  submitted = false;
  // pure-UI helper for the "none of these" chip; not a stored field, so the
  // analysis data model is unchanged (it just clears the symptom chips).
  noneSelected = false;
  loginService = inject(LoginService);
  excludedFields = [
    'age',
    'period_last_12',
    'last_period',
    'noBleedReasons',
    'surgery_procedure',
    'surgery_ovaries_removed',
    'surgery_year',
    'surgery_hormone_therapy',
    'surgery_hormone_year',
  ];

  // the 9 symptom chips: form-control key → i18n label key (same fields as before)
  symptomChips = [
    { key: 'heavy_flow', label: 'symptoms.symptomsList.heavyFlow' },
    { key: 'night_sweats', label: 'symptoms.symptomsList.nightSweats' },
    { key: 'shorter_cycles', label: 'symptoms.symptomsList.shorterCycles' },
    { key: 'breast_changes', label: 'symptoms.symptomsList.breastChanges' },
    { key: 'migraines', label: 'symptoms.symptomsList.migraines' },
    { key: 'weight_gain', label: 'symptoms.symptomsList.weightGain' },
    { key: 'mood_swings', label: 'symptoms.symptomsList.moodSwings' },
    { key: 'night_waking', label: 'symptoms.symptomsList.nightWaking' },
    { key: 'increased_cramps', label: 'symptoms.symptomsList.increasedCramps' },
  ];
  /** How long ago her last period was. Frequency-style buckets, never a date. */
  lastPeriodOptions = [
    { key: 'within_month', label: 'symptoms.lastPeriod.withinMonth' },
    { key: 'm1_3', label: 'symptoms.lastPeriod.m1_3' },
    { key: 'm3_6', label: 'symptoms.lastPeriod.m3_6' },
    { key: 'm6_12', label: 'symptoms.lastPeriod.m6_12' },
    { key: 'over_year', label: 'symptoms.lastPeriod.overYear' },
    { key: 'not_sure', label: 'symptoms.lastPeriod.notSure' },
  ];

  /**
   * Last period vs the 12-month bleeding answer (UX review 19.10, 1B): "yes, I
   * bled in the last 12 months" cannot pair with "more than a year ago", and
   * "no" can only pair with "more than a year ago" or "I do not remember".
   * The options stay the same keys; only the contradictory ones are hidden.
   */
  get visibleLastPeriodOptions() {
    const p = this.symptomsForm.get('period_last_12')?.value;
    if (p === 'yes') return this.lastPeriodOptions.filter((o) => o.key !== 'over_year');
    if (p === 'no')
      return this.lastPeriodOptions.filter((o) => o.key === 'over_year' || o.key === 'not_sure');
    return this.lastPeriodOptions;
  }

  /** the five top-level questions; follow-ups do not move the count (1C) */
  readonly progressTotal = 5;
  get answeredCount(): number {
    const v = this.symptomsForm.value;
    const age = this.symptomsForm.get('age');
    return [
      !!age?.value && age.valid,
      !!v.period_last_12,
      !!v.last_period,
      ((v.contraception_methods as string[]) || []).length > 0,
      this.hasRecentChangesAnswer,
    ].filter(Boolean).length;
  }

  /**
   * Which contraception she uses, asked of EVERYONE (Danna 22.09), not only a
   * woman with no bleeding. The types are split because a copper coil is not
   * hormonal at all and moves the cycle differently from a pill or a hormonal
   * coil, so one yes/no answer cannot be read (Revital item 12).
   *
   * Multi-select, because a real answer can be more than one. The last three
   * are exclusive: they are answers ABOUT the list, not items in it.
   */
  contraceptionOptions = [
    { key: 'hormonal_iud', label: 'symptoms.contraception.opts.hormonalIud' },
    { key: 'copper_iud', label: 'symptoms.contraception.opts.copperIud' },
    { key: 'pill', label: 'symptoms.contraception.opts.pill' },
    { key: 'patch', label: 'symptoms.contraception.opts.patch' },
    { key: 'ring', label: 'symptoms.contraception.opts.ring' },
    { key: 'injection', label: 'symptoms.contraception.opts.injection' },
    { key: 'other', label: 'symptoms.contraception.opts.other' },
    { key: 'none', label: 'symptoms.contraception.opts.none' },
    { key: 'not_sure', label: 'symptoms.contraception.opts.notSure' },
    { key: 'prefer_not', label: 'symptoms.contraception.opts.preferNot' },
  ];
  /** Answers about the list rather than items in it, so they stand alone. */
  private readonly contraceptionExclusive = ['none', 'not_sure', 'prefer_not'];

  noBleedReasonOptions = [
    { key: 'natural_menopause', label: 'symptoms.noBleedReasons.naturalMenopause' },
    { key: 'contraception', label: 'symptoms.noBleedReasons.contraception' },
    { key: 'surgical', label: 'symptoms.noBleedReasons.surgical' },
    { key: 'other_medical', label: 'symptoms.noBleedReasons.otherMedical' },
  ];

  // Surgery branch. Every control here is optional: the reason chips above are
  // optional too, so making their children mandatory would block Continue on a
  // question she was never obliged to reach.
  surgeryProcedureOptions: { key: SurgeryProcedure; label: string }[] = [
    { key: 'hysterectomy_only', label: 'symptoms.surgery.procedureOptions.hysterectomyOnly' },
    { key: 'ovaries_only', label: 'symptoms.surgery.procedureOptions.ovariesOnly' },
    {
      key: 'hysterectomy_and_ovaries',
      label: 'symptoms.surgery.procedureOptions.hysterectomyAndOvaries',
    },
    { key: 'not_sure', label: 'symptoms.surgery.procedureOptions.notSure' },
  ];
  ovaryCountOptions = [
    { key: 'one', label: 'symptoms.surgery.ovaryCount.one' },
    { key: 'two', label: 'symptoms.surgery.ovaryCount.two' },
    { key: 'not_sure', label: 'symptoms.surgery.ovaryCount.notSure' },
  ];
  hormoneOptions = [
    { key: 'yes', label: 'common.buttons.yes' },
    { key: 'no', label: 'common.buttons.no' },
    { key: 'not_sure', label: 'symptoms.surgery.hormoneNotSure' },
  ];
  // Danna asked for a year, not a full date. Computed from today so the range
  // never goes stale.
  readonly maxYear = new Date().getFullYear();
  readonly minYear = new Date().getFullYear() - 80;
  /** the adult range the age field accepts (correction brief 12.1) */
  readonly minAge = 18;
  readonly maxAge = 99;
  private readonly surgeryControls = [
    'surgery_procedure',
    'surgery_ovaries_removed',
    'surgery_year',
    'surgery_hormone_therapy',
    'surgery_hormone_year',
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private restService: RestService,
    private sessionService: SessionService,
    private translationService: TranslationService
  ) {
    this.scanId = this.route.snapshot.params['scanId'];
    this.initForm();
  }

  private initForm() {
    this.symptomsForm = this.fb.group({
      age: [null as number | null, [Validators.required, this.ageInRange]],
      period_last_12: [null as YesNoValue | null, [Validators.required]],
      // Optional: how long ago the last period was. Left empty it stays 'not
      // asked' and the report shows no line for it, never a guess.
      last_period: [null as string | null],
      noBleedReasons: [[] as string[]],
      // Optional: never force a woman to state her contraception to continue.
      contraception_methods: [[] as string[]],
      heavy_flow: [false],
      night_sweats: [false],
      shorter_cycles: [false],
      breast_changes: [false],
      migraines: [false],
      weight_gain: [false],
      mood_swings: [false],
      night_waking: [false],
      increased_cramps: [false],
      other_enabled: [false],
      other_text: [''],
      surgery_procedure: [null as SurgeryProcedure | null],
      surgery_ovaries_removed: [null as string | null],
      surgery_year: [null as number | null, [this.yearInRange]],
      surgery_hormone_therapy: [null as string | null],
      surgery_hormone_year: [null as number | null, [this.yearInRange]],
    });
  }

  /**
   * Correction brief 12.1. The field had only `required` plus min/max attributes,
   * which a number input does not enforce, so 4 or 300 went through and every
   * age-shaped comparison downstream inherited it. Empty stays valid so the
   * required error keeps owning the empty case and she is not told two things at
   * once.
   */
  private ageInRange = (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const age = Number(raw);
    if (!Number.isInteger(age) || age < this.minAge || age > this.maxAge) {
      return { ageRange: true };
    }
    return null;
  };

  /**
   * Only complains about a year she actually typed. Empty stays valid, because
   * the whole surgery branch is optional and an invisible error on a hidden
   * control would silently disable Continue.
   */
  private yearInRange = (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const year = Number(raw);
    if (!Number.isInteger(year) || year < this.minYear || year > this.maxYear) {
      return { yearRange: true };
    }
    return null;
  };

  setYesNo(name: string, val: YesNoValue) {
    this.symptomsForm.get(name)?.setValue(val);
    if (name === 'period_last_12' && val !== 'no') {
      this.symptomsForm.get('noBleedReasons')?.setValue([]);
      // the surgery branch lives inside the no-bleeding reasons, so it goes
      // with them - otherwise a stale answer would still be submitted.
      this.clearSurgeryFollowup();
    }
    // drop a last-period answer the new bleeding answer contradicts
    const lp = this.symptomsForm.get('last_period');
    if (name === 'period_last_12' && lp?.value &&
        !this.visibleLastPeriodOptions.some((o) => o.key === lp.value)) {
      lp.setValue(null);
    }
  }

  setLastPeriod(key: string) {
    const ctrl = this.symptomsForm.get('last_period');
    ctrl?.setValue(ctrl.value === key ? null : key);
  }

  toggleContraception(key: string) {
    const ctrl = this.symptomsForm.get(
      'contraception_methods',
    ) as FormControl<string[]>;
    const current = ctrl?.value || [];
    if (current.includes(key)) {
      ctrl?.setValue(current.filter((item) => item !== key));
      return;
    }
    // "none" / "not sure" / "prefer not" answer the whole question, so picking
    // one clears the methods, and picking a method clears them.
    if (this.contraceptionExclusive.includes(key)) {
      ctrl?.setValue([key]);
      return;
    }
    ctrl?.setValue([
      ...current.filter((item) => !this.contraceptionExclusive.includes(item)),
      key,
    ]);
  }

  isContraceptionSelected(key: string): boolean {
    return (
      (this.symptomsForm.get('contraception_methods')?.value || []) as string[]
    ).includes(key);
  }

  toggleNoBleedReason(key: string) {
    const ctrl = this.symptomsForm.get('noBleedReasons') as FormControl<string[]>;
    const current = ctrl?.value || [];
    const next = current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key];
    ctrl?.setValue(next);
    if (key === 'surgical' && !next.includes('surgical')) {
      this.clearSurgeryFollowup();
    }
  }

  get showSurgeryFollowup(): boolean {
    return (
      this.symptomsForm.get('period_last_12')?.value === 'no' &&
      this.isNoBleedReasonSelected('surgical')
    );
  }

  get showOvaryCount(): boolean {
    const procedure = this.symptomsForm.get('surgery_procedure')
      ?.value as SurgeryProcedure | null;
    return !!procedure && PROCEDURES_WITH_OVARY_REMOVAL.includes(procedure);
  }

  get showHormoneYear(): boolean {
    return this.symptomsForm.get('surgery_hormone_therapy')?.value === 'yes';
  }

  setSurgeryProcedure(key: SurgeryProcedure) {
    this.symptomsForm.get('surgery_procedure')?.setValue(key);
    // a procedure that keeps the ovaries hides the count question, so drop the
    // answer instead of submitting one she can no longer see.
    if (!this.showOvaryCount) {
      this.symptomsForm.get('surgery_ovaries_removed')?.setValue(null);
    }
  }

  setOvaryCount(key: string) {
    this.symptomsForm.get('surgery_ovaries_removed')?.setValue(key);
  }

  setHormoneTherapy(key: string) {
    this.symptomsForm.get('surgery_hormone_therapy')?.setValue(key);
    if (key !== 'yes') {
      this.symptomsForm.get('surgery_hormone_year')?.setValue(null);
    }
  }

  private clearSurgeryFollowup() {
    this.surgeryControls.forEach((name) =>
      this.symptomsForm.get(name)?.setValue(null)
    );
  }

  yearError(name: string): boolean {
    const control = this.symptomsForm.get(name);
    return !!control?.errors?.['yearRange'] && (this.submitted || !!control.touched);
  }

  isNoBleedReasonSelected(key: string): boolean {
    return ((this.symptomsForm.get('noBleedReasons')?.value || []) as string[]).includes(key);
  }

  toggleSymptom(key: string) {
    const ctrl = this.symptomsForm.get(key);
    const next = !ctrl?.value;
    ctrl?.setValue(next);
    if (next) this.noneSelected = false;
  }

  toggleOther() {
    const ctrl = this.symptomsForm.get('other_enabled');
    const next = !ctrl?.value;
    ctrl?.setValue(next);
    if (next) this.noneSelected = false;
    else this.symptomsForm.get('other_text')?.setValue('');
  }

  toggleNone() {
    this.noneSelected = !this.noneSelected;
    if (this.noneSelected) {
      this.symptomChips.forEach((c) =>
        this.symptomsForm.get(c.key)?.setValue(false)
      );
      this.symptomsForm.get('other_enabled')?.setValue(false);
      this.symptomsForm.get('other_text')?.setValue('');
    }
  }

  get showAgeNote(): boolean {
    const a = this.symptomsForm.get('age')?.value;
    return a != null && (a < 35 || a > 55);
  }

  get hasRecentChangesAnswer(): boolean {
    if (this.noneSelected) return true;
    if (this.symptomsForm.get('other_enabled')?.value) return true;
    return this.symptomChips.some((c) => this.symptomsForm.get(c.key)?.value === true);
  }

  get canSubmit(): boolean {
    return !this.isSubmitting && this.symptomsForm.valid && this.hasRecentChangesAnswer;
  }

  /**
   * The surgery branch as it is actually shown: a question she cannot see right
   * now contributes nothing, so the timeline that reaches the server always
   * matches the screen she filled in. Null when the branch is closed entirely.
   */
  private surgeryPayload() {
    if (!this.showSurgeryFollowup) return null;
    const v = this.symptomsForm.value;
    const year = (raw: unknown) => {
      const n = Number(raw);
      return raw === null || raw === undefined || raw === '' || !Number.isInteger(n)
        ? null
        : n;
    };
    return {
      procedure: v.surgery_procedure || null,
      ovaries_removed: this.showOvaryCount ? v.surgery_ovaries_removed || null : null,
      year: year(v.surgery_year),
      hormone_therapy: v.surgery_hormone_therapy || null,
      hormone_therapy_year: this.showHormoneYear ? year(v.surgery_hormone_year) : null,
    };
  }

  async saveAndNavigate(selectedSymptoms: string[]) {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    try {
      await this.restService.request({
        path: `scan/${this.scanId}/symptoms`,
        method: 'POST',
        body: {
          symptoms: selectedSymptoms,
          age: +this.symptomsForm.value.age,
          period_last_12: this.symptomsForm.value.period_last_12,
          last_period: this.symptomsForm.value.last_period || null,
          noBleedReasons:
            this.symptomsForm.value.period_last_12 === 'no'
              ? this.symptomsForm.value.noBleedReasons || []
              : [],
          contraception_methods:
            this.symptomsForm.value.contraception_methods || [],
          surgery: this.surgeryPayload(),
        },
      });

      // Straight into the symptom map: the steps overview between the
      // questionnaire and the symptom map was dropped for a shorter flow
      // (Danna 19.10). The overview still shows on resume and between later steps.
      await this.router.navigate(['/app/symptoms-form', this.scanId], {
        replaceUrl: true,
      });
    } catch (error) {
      console.error('Failed to save symptoms:', error);
    } finally {
      this.isSubmitting = false;
    }
  }

  onSubmit() {
    this.submitted = true;
    if (this.symptomsForm.invalid) {
      this.symptomsForm.markAllAsTouched();
      return;
    }
    if (!this.hasRecentChangesAnswer) {
      return;
    }

    const selectedSymptoms = Object.entries(this.symptomsForm.value)
      .filter(
        ([key, value]) => value === true && !this.excludedFields.includes(key)
      )
      .map(([key]) => key);

    if (
      this.symptomsForm.value.other_enabled &&
      this.symptomsForm.value.other_text
    ) {
      selectedSymptoms.push(this.symptomsForm.value.other_text);
    }

    if (selectedSymptoms.length === 0) {
      // explicit "none" → save directly; otherwise confirm she has no symptoms
      if (this.noneSelected) this.saveAndNavigate([]);
      else this.openConfirmDialog();
    } else {
      this.saveAndNavigate(selectedSymptoms);
    }
  }

  private openConfirmDialog() {
    const dialogRef = this.dialog.open(NoSymptomsDialogComponent, {
      width: '400px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.saveAndNavigate([]);
      }
    });
  }

  get ageError(): string {
    const control = this.symptomsForm.get('age');
    if (control?.errors?.['ageRange']) {
      return this.translationService.translate('symptoms.ageRange', {
        min: this.minAge,
        max: this.maxAge,
      });
    }
    if (control?.errors?.['required']) {
      return this.translationService.translate('symptoms.ageRequired');
    }
    return '';
  }

  /**
   * An out-of-range age is answered the moment she leaves the field, so she is
   * not carried to the end of the questionnaire before hearing about it. An
   * empty field only complains on submit, so tabbing through says nothing.
   */
  get showAgeError(): boolean {
    const control = this.symptomsForm.get('age');
    if (!control || control.valid) return false;
    if (control.errors?.['ageRange']) return control.touched || this.submitted;
    return this.submitted;
  }
}
